import { SelectionModel } from '@angular/cdk/collections';
import { ScrollingModule } from '@angular/cdk/scrolling';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  InputSignal,
  ModelSignal,
  OutputEmitterRef,
  Signal,
  computed,
  effect,
  input,
  model,
  output,
} from '@angular/core';
import { MatCheckboxModule } from '@angular/material/checkbox';

import { FilterSelectionOption } from '../../models';
import {
  FilterOptionComponent,
  OptionLabelTemplateContext,
  OptionLabelTemplateRef,
} from '../filter-option/filter-option.component';

@Component({
  selector: 'mp-multi-selection-filter',
  templateUrl: './multi-selection-filter.component.html',
  styleUrl: './multi-selection-filter.component.scss',
  host: {
    '[style.height]': 'listHeight()',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatCheckboxModule, FilterOptionComponent, ScrollingModule],
})
export class MultiSelectionFilterComponent<T> {
  private readonly DEFAULT_ITEM_SIZE = 40;
  private readonly DEFAULT_MAX_VISIBLE_ITEMS = 5;

  readonly options: InputSignal<FilterSelectionOption<T>[]> = input.required<FilterSelectionOption<T>[]>();

  readonly selectedValues: ModelSignal<T[]> = model<T[]>([]);

  readonly optionLabelTemplate: InputSignal<OptionLabelTemplateRef<T> | undefined> = input<OptionLabelTemplateRef<T>>();

  readonly itemSize: InputSignal<number> = input<number>(this.DEFAULT_ITEM_SIZE);

  readonly virtualScrollMaxVisibleItems: InputSignal<number> = input<number>(this.DEFAULT_MAX_VISIBLE_ITEMS);

  readonly optionSelect: OutputEmitterRef<FilterSelectionOption<T>> = output<FilterSelectionOption<T>>();

  readonly optionLabelTemplateContextType!: OptionLabelTemplateContext<T>;

  protected readonly selectionModel: SelectionModel<T> = new SelectionModel<T>(true, []);

  protected readonly listHeight: Signal<string> = computed(() => this.getListHeight());

  constructor(private readonly cdr: ChangeDetectorRef) {
    effect(() => {
      this.selectionModel.setSelection(...this.selectedValues());
      this.cdr.markForCheck();
    });
  }

  onFilterItemChanges(value: T): void {
    this.selectionModel.toggle(value);
    this.selectedValues.set(this.selectionModel.selected);
  }

  onOptionSelect(option: FilterSelectionOption<T>, checked: boolean): void {
    if (checked) {
      this.optionSelect.emit(option);
    }
  }

  trackByFn(_: number, option: FilterSelectionOption<T>): string {
    return option.value + '';
  }

  private getListHeight(): string {
    const optionsNumber: number = Math.min(this.options().length, this.virtualScrollMaxVisibleItems());
    if (optionsNumber === 0) {
      return 'auto';
    }

    const height: number = optionsNumber * this.itemSize();
    return `${height}px`;
  }
}
